import React, { useState, useEffect, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { 
  Search, 
  X, 
  Users, 
  ChevronDown, 
  Upload, 
  UserPlus,
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight 
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../constants/api';
import { MembershipsTable, MemberDetailsDrawer, BulkUploadDrawer, StatsDashboard, CreateMemberDrawer, type Membership } from '../components/memberships';
import { usePermissions } from '../hooks/usePermissions';

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

interface PaginatedMembershipsResponse {
  data: Membership[];
  meta: PaginationMeta;
}

interface StatsData {
  totalMembers: number;
  genderStats: {
    hombres: number;
    mujeres: number;
  };
  civilStatusStats: {
    soltero: number;
    soltera: number;
    casado: number;
    casada: number;
    viudo: number;
    viuda: number;
    divorciado: number;
    divorciada: number;
  };
  ageStats: {
    menoresDeEdad: number;
    mayoresDeEdad: number;
    detalleEdad: {
      ninos: number;
      adolescentes: number;
      jovenes: number;
      adultosJovenes: number;
      adultos: number;
      adultosMayores: number;
    };
  };
  zonaStats: Array<{ zona: string; count: number }>;
  documentoStats: {
    conDocumento: number;
    sinDocumento: number;
    porTipo: {
      cedula: number;
      tarjetaIdentidad: number;
      pasaporte: number;
      otro: number;
    };
  };
  registroPorMes: Array<{ mes: string; anio: number; count: number }>;
  // Estadísticas de datos complementarios
  spiritualStats: {
    totalConDatosComplementarios: number;
    bautizados: number;
    noBautizados: number;
    oracionFe: number;
    sinOracionFe: number;
    perteneceMinisterio: number;
    noPerteneceMinisterio: number;
    topMinisterios: Array<{ ministerio: string; count: number }>;
    topHabilidades: Array<{ habilidad: string; count: number }>;
    topDones: Array<{ don: string; count: number }>;
    distribucionTiempoAsistencia: Array<{ tiempo: string; count: number }>;
  };
  academicStats: {
    distribucionNivelEstudios: Array<{ nivel: string; count: number }>;
    topProfesiones: Array<{ profesion: string; count: number }>;
    conProfesion: number;
    sinProfesion: number;
  };
  personalityStats: {
    distribucionPersonalidad: Array<{ tipo: string; count: number }>;
  };
}

const fetchMemberships = async (page: number = 1, limit: number = 50, searchTerm?: string): Promise<PaginatedMembershipsResponse> => {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString()
  });

  if (searchTerm && searchTerm.trim()) {
    params.append('search', searchTerm.trim());
  }

  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.list}?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch memberships');
  }

  const result = await response.json();
  // El backend retorna { data: { data: [...], meta: {...} } }
  return result.data;
};

const fetchStats = async (): Promise<StatsData> => {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.list}/stats`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch stats');
  }

  const result = await response.json();
  // El backend retorna { data: { ... } } como el endpoint de memberships
  return result.data;
};

export const MembresíasPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [selectedMember, setSelectedMember] = useState<Membership | null>(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [bulkUploadType, setBulkUploadType] = useState<'basicos' | 'complementarios'>('basicos');
  const [isCreateMemberOpen, setIsCreateMemberOpen] = useState(false);
  const [isBulkUploadDropdownOpen, setIsBulkUploadDropdownOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(7);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'list' | 'stats'>('list');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isDrawerOpen = selectedMember !== null;
  
  // Cerrar drawer cuando se desmonta el componente (navegación)
  useEffect(() => {
    return () => {
      setSelectedMember(null);
    };
  }, []);

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms de delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Resetear página cuando cambie el término de búsqueda debounced
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  // Cerrar dropdown cuando se hace clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
      if (isBulkUploadDropdownOpen && !target.closest('[data-bulk-dropdown]')) {
        setIsBulkUploadDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isBulkUploadDropdownOpen]);

  const { 
    data, 
    isLoading, 
    error,
    refetch,
    isFetching
  } = useQuery<PaginatedMembershipsResponse>({
    queryKey: ['memberships', page, limit, debouncedSearchTerm],
    queryFn: () => fetchMemberships(page, limit, debouncedSearchTerm),
    placeholderData: keepPreviousData,
  });

  const memberships = data?.data || [];
  const paginationMeta = data?.meta;

  // Query para estadísticas
  const { 
    data: statsData, 
    isLoading: isLoadingStats 
  } = useQuery<StatsData>({
    queryKey: ['membership-stats'],
    queryFn: fetchStats,
    enabled: activeTab === 'stats', // Solo cargar cuando esté en la pestaña de estadísticas
  });

  // Validación adicional para asegurar que memberships sea siempre un array
  const safeMemberships = Array.isArray(memberships) ? memberships : [];

  // Estado para mostrar loading solo en el input mientras se escribe (antes del debounce)
  const isSearching = isFetching && !!debouncedSearchTerm;

  const handleMemberClick = (member: Membership) => {
    setSelectedMember(member);
  };

  const handleCloseDrawer = () => {
    setSelectedMember(null);
  };

  const handleOpenBulkUpload = (type: 'basicos' | 'complementarios' = 'basicos') => {
    setBulkUploadType(type);
    setIsBulkUploadOpen(true);
    setIsBulkUploadDropdownOpen(false);
  };

  const handleCloseBulkUpload = () => {
    setIsBulkUploadOpen(false);
  };

  const handleOpenCreateMember = () => {
    setIsCreateMemberOpen(true);
  };

  const handleCloseCreateMember = () => {
    setIsCreateMemberOpen(false);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePreviousPage = () => {
    if (paginationMeta?.hasPrevious) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationMeta?.hasNext) {
      setPage(page + 1);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Resetear a la primera página cuando cambie el límite
  };

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };


  // Loading inicial: solo muestra spinner de pantalla completa en la primera carga real
  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xs mb-4">Error al cargar las membresías</div>
          <button 
            onClick={() => refetch()}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
        {/* Pestañas minimalistas */}
        <div className="flex space-x-4 md:space-x-8 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('list')}
            className={`py-2 px-1 text-xs sm:text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'list'
                ? 'text-purple-700 border-purple-700'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Lista de Miembros
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`py-2 px-1 text-xs sm:text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'stats'
                ? 'text-purple-700 border-purple-700'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Dashboard Estadístico
          </button>
        </div>
        
        {/* Barra de búsqueda - Solo mostrar en la pestaña de lista */}
        {activeTab === 'list' && (
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 w-full sm:max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {isFetching && searchTerm ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent"></div>
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Buscar por nombre, email, teléfono o documento..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-xs placeholder-gray-500 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => handleSearchChange('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2 md:space-x-3 flex-wrap gap-y-2">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200/50 rounded-lg px-2.5 sm:px-3 md:px-4 py-2">
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                  <div className="text-xs sm:text-xs">
                    {searchTerm ? (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium text-purple-700">{paginationMeta ? paginationMeta.total : safeMemberships.length}</span>
                        <span className="hidden sm:inline text-gray-600">resultados</span>
                        <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-500 ml-0.5" />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium text-purple-700">{paginationMeta ? paginationMeta.total : safeMemberships.length}</span>
                        <span className="hidden sm:inline text-gray-600">miembros</span>
                        <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-500 ml-0.5" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Selector de cantidad por página - Elegante */}
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <span className="hidden sm:inline text-xs text-gray-500 font-medium">Mostrar</span>
                <div className="relative" data-dropdown>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="bg-white border border-gray-300 rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2 pr-7 sm:pr-8 text-xs sm:text-xs font-medium text-gray-700 hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all cursor-pointer flex items-center min-w-[3rem]"
                  >
                    {limit}
                  </button>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-1.5 sm:pr-2 pointer-events-none">
                    <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </div>
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                      {[10, 20, 30, 50, 100].map((value) => (
                        <div
                          key={value}
                          onClick={() => {
                            handleLimitChange(value);
                            setIsDropdownOpen(false);
                          }}
                          className="px-3 py-2 text-xs font-medium text-purple-700 hover:bg-purple-50 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {value}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Botón Crear Miembro */}
              {hasPermission('Membresías', 'crear') && (
                <button
                  onClick={handleOpenCreateMember}
                  className="bg-green-600 text-white px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1 sm:space-x-2"
                >
                  <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-xs">Crear</span>
                </button>
              )}
              
              {/* Dropdown Carga Masiva */}
              {hasPermission('Membresías', 'importar') && (
                <div className="relative" data-bulk-dropdown>
                  <button
                    onClick={() => setIsBulkUploadDropdownOpen(!isBulkUploadDropdownOpen)}
                    className="bg-purple-600 text-white px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-1 sm:space-x-2"
                  >
                    <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-xs">Cargar</span>
                    <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ml-0.5 sm:ml-1 transition-transform ${isBulkUploadDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isBulkUploadDropdownOpen && (
                    <div className="absolute top-full right-0 mt-2 w-60 sm:w-64 bg-white border border-gray-300 rounded-lg shadow-xl z-20">
                      <div className="py-2">
                        <button
                          onClick={() => handleOpenBulkUpload('basicos')}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-left hover:bg-purple-50 transition-colors flex items-center space-x-2 sm:space-x-3 text-gray-700 hover:text-purple-600"
                        >
                          <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 shrink-0" />
                          <div>
                            <div className="font-medium text-xs sm:text-xs">Datos Básicos</div>
                            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Información personal y contacto</div>
                          </div>
                        </button>
                        
                        <button
                          onClick={() => handleOpenBulkUpload('complementarios')}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-left hover:bg-purple-50 transition-colors flex items-center space-x-2 sm:space-x-3 text-gray-700 hover:text-purple-600"
                        >
                          <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 shrink-0" />
                          <div>
                            <div className="font-medium text-xs sm:text-xs">Datos Complementarios</div>
                            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Estudios, ministerios y habilidades</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Contenido de las pestañas */}
      {activeTab === 'list' ? (
        <>
          {/* Tabla de miembros */}
          <MembershipsTable 
            memberships={safeMemberships}
            onMemberClick={handleMemberClick}
            isLoading={isLoading && safeMemberships.length === 0}
          />

          {/* Paginación Elegante */}
          {paginationMeta && (
            <div className="px-4 lg:px-6 py-4 lg:py-6 border-t border-gray-100 bg-gray-50/30">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                {/* Información de resultados */}
                <div className="flex items-center space-x-4 md:space-x-6">
              <div className="text-xs md:text-xs text-gray-600">
                <span className="font-medium text-gray-900">
                  {((paginationMeta.page - 1) * paginationMeta.limit) + 1}
                </span>
                <span className="mx-1">–</span>
                <span className="font-medium text-gray-900">
                  {Math.min(paginationMeta.page * paginationMeta.limit, paginationMeta.total)}
                </span>
                <span className="mx-1 hidden sm:inline">de</span>
                <span className="hidden sm:inline font-medium text-gray-900">{paginationMeta.total}</span>
                <span className="ml-1 hidden sm:inline">resultados</span>
              </div>
              
              {/* Páginas X de Y */}
              <div className="hidden md:block text-xs text-gray-500">
                Página <span className="font-medium text-gray-900">{paginationMeta.page}</span> de <span className="font-medium text-gray-900">{paginationMeta.totalPages}</span>
              </div>
            </div>
            
            {/* Controles de paginación */}
            <div className="flex items-center space-x-1">
              {/* Botón primera página */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={!paginationMeta.hasPrevious}
                className={`hidden sm:flex w-9 h-9 items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Primera página"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>

              {/* Botón página anterior */}
              <button
                onClick={handlePreviousPage}
                disabled={!paginationMeta.hasPrevious}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Páginas centrales */}
              <div className="flex items-center space-x-1 mx-1 md:mx-2">
                {(() => {
                  const pages = [];
                  const maxVisiblePages = typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : 3;
                  let start = Math.max(1, paginationMeta.page - Math.floor(maxVisiblePages / 2));
                  let end = Math.min(paginationMeta.totalPages, start + maxVisiblePages - 1);
                  
                  start = Math.max(1, end - maxVisiblePages + 1);

                  // Mostrar primera página si no está en el rango
                  if (start > 1) {
                    pages.push(
                      <button
                        key="1"
                        onClick={() => handlePageChange(1)}
                        className="hidden sm:flex w-9 h-9 items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        1
                      </button>
                    );
                    if (start > 2) {
                      pages.push(
                        <span key="ellipsis-start" className="hidden sm:flex w-9 h-9 items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                  }

                  // Páginas del rango central
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`w-9 h-9 flex items-center justify-center text-xs font-medium rounded-lg transition-all ${
                          i === paginationMeta.page
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Mostrar última página si no está en el rango
                  if (end < paginationMeta.totalPages) {
                    if (end < paginationMeta.totalPages - 1) {
                      pages.push(
                        <span key="ellipsis-end" className="hidden sm:flex w-9 h-9 items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                    pages.push(
                      <button
                        key={paginationMeta.totalPages}
                        onClick={() => handlePageChange(paginationMeta.totalPages)}
                        className="hidden sm:flex w-9 h-9 items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        {paginationMeta.totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              {/* Botón página siguiente */}
              <button
                onClick={handleNextPage}
                disabled={!paginationMeta.hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Botón última página */}
              <button
                onClick={() => handlePageChange(paginationMeta.totalPages)}
                disabled={!paginationMeta.hasNext}
                className={`hidden sm:flex w-9 h-9 items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Última página"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Dashboard Estadístico */
        <div className="p-6">
          <StatsDashboard 
            stats={statsData || {
              totalMembers: 0,
              genderStats: { hombres: 0, mujeres: 0 },
              civilStatusStats: { 
                soltero: 0, soltera: 0, casado: 0, casada: 0, 
                viudo: 0, viuda: 0, divorciado: 0, divorciada: 0 
              },
              ageStats: { 
                menoresDeEdad: 0, 
                mayoresDeEdad: 0,
                detalleEdad: { ninos: 0, adolescentes: 0, jovenes: 0, adultosJovenes: 0, adultos: 0, adultosMayores: 0 }
              },
              zonaStats: [],
              documentoStats: { conDocumento: 0, sinDocumento: 0, porTipo: { cedula: 0, tarjetaIdentidad: 0, pasaporte: 0, otro: 0 } },
              registroPorMes: [],
              spiritualStats: {
                totalConDatosComplementarios: 0,
                bautizados: 0,
                noBautizados: 0,
                oracionFe: 0,
                sinOracionFe: 0,
                perteneceMinisterio: 0,
                noPerteneceMinisterio: 0,
                topMinisterios: [],
                topHabilidades: [],
                topDones: [],
                distribucionTiempoAsistencia: []
              },
              academicStats: {
                distribucionNivelEstudios: [],
                topProfesiones: [],
                conProfesion: 0,
                sinProfesion: 0
              },
              personalityStats: {
                distribucionPersonalidad: []
              }
            }}
            isLoading={isLoadingStats}
          />
        </div>
      )}

      {/* Drawer de detalles del miembro */}
      <MemberDetailsDrawer 
        isOpen={isDrawerOpen}
        member={selectedMember}
        onClose={handleCloseDrawer}
      />

      {/* Drawer de carga masiva */}
      <BulkUploadDrawer 
        isOpen={isBulkUploadOpen}
        onClose={handleCloseBulkUpload}
        onSuccess={() => refetch()}
        uploadType={bulkUploadType}
      />

      {/* Drawer de crear miembro */}
      <CreateMemberDrawer 
        isOpen={isCreateMemberOpen}
        onClose={handleCloseCreateMember}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default MembresíasPage;